import { createContext, type ReactNode, useContext, useReducer } from "react";
import {
  CRITICALITIES,
  CRITICALITY_TYPES,
  type Criticality,
  type CriticalityType,
  type EconomicZone,
} from "@/types/FilterConstants";

export type StateTableFilterContextAction =
  | {
      type: "setEconomicZones";
      payload: { newFilters: Array<EconomicZone["value"]> };
    }
  | {
      type: "setHMSCriticalities";
      payload: { newFilters: Array<Criticality["value"]> };
    }
  | {
      type: "setDRCriticalities";
      payload: { newFilters: Array<Criticality["value"]> };
    }
  | { type: "setIpStates"; payload: { ipStates: number[] } }
  | { type: "setIpTypes"; payload: { ipTypes: string[] } }
  | { type: "resetFilters" }
  | {
      type: "setCriticalityType";
      payload: { newFilters: CriticalityType["value"] };
    }
  | {
      type: "setCriticalities";
      payload: { newFilters: Array<Criticality["value"]> };
    }
  | {
      type: "setOnlyDeviations";
      payload: { value: boolean };
    };

export type Dispatch = (action: StateTableFilterContextAction) => void;
export type IP_FilterProviderProps = { children: ReactNode };

export type StateTableFilterContextState = {
  economicZones: Array<EconomicZone["value"]>;
  ipStates: number[];
  ipTypes: string[];
  criticalityType: CriticalityType["value"];
  criticalities: Array<Criticality["value"]>;
  onlyDeviations?: boolean;
};

const initialState: StateTableFilterContextState = {
  economicZones: [],
  ipStates: [],
  ipTypes: [],
  criticalityType: CRITICALITY_TYPES[0].value,
  criticalities: CRITICALITIES.map((criticality) => criticality.value)
    .sort()
    .filter((criticality) => criticality > 0), // Default don't show TK0 - Ukjent
  onlyDeviations: true, // Default show all states
};

const StateTableFilterStateContext = createContext<StateTableFilterContextState>(initialState);
const StateTableFilterDispatchContext = createContext<Dispatch | undefined>(undefined);

const reducer = (state: StateTableFilterContextState, action: StateTableFilterContextAction) => {
  switch (action.type) {
    case "setEconomicZones":
      return {
        ...state,
        economicZones: action.payload.newFilters,
      };
    case "setIpStates":
      return {
        ...state,
        ipStates: action.payload.ipStates,
      };
    case "setIpTypes":
      return {
        ...state,
        ipTypes: action.payload.ipTypes,
      };
    case "resetFilters":
      return {
        ...initialState,
      };
    case "setCriticalityType":
      return {
        ...state,
        criticalityType: action.payload.newFilters,
      };
    case "setCriticalities":
      return {
        ...state,
        criticalities: action.payload.newFilters,
      };
    case "setOnlyDeviations":
      return {
        ...state,
        onlyDeviations: action.payload.value,
      };
    default:
      return { ...state };
  }
};

export const StateTableProvider = (props: IP_FilterProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  return (
    <StateTableFilterStateContext.Provider value={state}>
      <StateTableFilterDispatchContext.Provider value={dispatch}>
        {props.children}
      </StateTableFilterDispatchContext.Provider>
    </StateTableFilterStateContext.Provider>
  );
};

export const useStateTableFilterState = (): StateTableFilterContextState => {
  const context = useContext(StateTableFilterStateContext);
  if (context === undefined) {
    throw new Error("useStateTableFilterState must be used within a StateTableProvider");
  }
  return context;
};

export const useStateTableFilterDispatch = (): Dispatch => {
  const context = useContext(StateTableFilterDispatchContext);
  if (context === undefined) {
    throw new Error("useStateTableFilterDispatch must be used within a StateTableProvider");
  }
  return context;
};
